import { documentWriteError } from "./documentWriteError";
import { apiUrl } from "../utils/api";
import { istLeer, type PdfBearbeitung } from "../components/documents/pdf-editor/pdfBearbeitung";

/** Eine frühere Fassung eines Dokuments (Spiegel des Backend-`DocumentVersionInfo`). */
export interface DocumentVersionInfo {
    id: number;
    versionNo: number;
    fileName: string;
    fileSize: number | null;
    createdAt: string;
    replacedByUserId: string | null;
    /** Aufgelöster Anzeigename; `null`, wenn der Nutzer nicht (mehr) existiert. */
    replacedByUserName: string | null;
    note: string | null;
    /** Was beim Ablösen geändert wurde („um 90° gedreht · 2 Bereiche entfernt“) — fehlt beim Bestand. */
    aenderung?: string | null;
    speWorkspaceId?: string | null;
    speRemoteVersionId?: string | null;
    speModifiedAt?: string | null;
    speModifiedByObjectId?: string | null;
    contentHash?: string | null;
}

/**
 * Obergrenze für eine gespeicherte Fassung — PDF-Editor, Bild-Speichern und
 * Seiten-Organisator prüfen vorab dagegen, damit statt eines nackten 413 eine
 * klare Meldung kommt.
 *
 * T-379 (Markus Felder): „Man kann nur max. 15 MB je PDF abspeichern. Wir
 * brauchen viel mehr." PO 29.10.2026: Speichern bis 100 MB. Spiegel von
 * `DOCUMENT_SAVE_SIZE_LIMIT_BYTES` im Backend (`speicher-grenze.ts`); der
 * Frontend-nginx lässt für `/versions` die Datei plus Ausschnitte durch.
 */
export const MAX_SPEICHERN_BYTES = 100 * 1024 * 1024;

/** Die verschobenen Ausschnitte eines Speicherns zusammen — Spiegel von `MAX_BILDER_GESAMT_BYTES` im Backend. */
const MAX_AUSSCHNITTE_BYTES = 20 * 1024 * 1024;

/** „höchstens 100 MB“ — für die Meldungen, aus der Grenze abgeleitet. */
export const hoechstensMb = (bytes: number = MAX_SPEICHERN_BYTES): string =>
    `höchstens ${Math.floor(bytes / 1024 / 1024)} MB`;

/**
 * Gemeinsame Angaben beim Speichern einer Fassung (User 07.10.2026).
 */
export interface FassungSpeichernAngaben {
    note?: string;
    fileName?: string;
    /** Was geändert wurde — steht in der Historie unter dem Kommentar. */
    aenderung?: string;
    /** Die Bytes stammen aus dieser früheren Fassung, nicht aus dem aktuellen Stand. */
    basisFassung?: number;
    /** Nicht als neueste Fassung, sondern als eigenes Dokument daneben speichern. */
    alsNeuesDokument?: boolean;
}

/** Antwort des Servers: `neuesDokument` nur bei „als eigenes Dokument“. */
export interface FassungGespeichert {
    documentId: number;
    neuesDokument?: boolean;
    fileName?: string;
}

function haengeAngabenAn(fd: FormData, o?: FassungSpeichernAngaben): void {
    if (o?.note) fd.append("note", o.note);
    if (o?.fileName) fd.append("fileName", o.fileName);
    if (o?.aenderung) fd.append("aenderung", o.aenderung);
    if (o?.basisFassung) fd.append("basisFassung", String(o.basisFassung));
    if (o?.alsNeuesDokument) fd.append("alsNeuesDokument", "1");
}

async function sendeFassung(documentId: number, fd: FormData): Promise<FassungGespeichert> {
    const res = await fetch(apiUrl(`documents/${documentId}/versions`), {
        method: "POST",
        body: fd,
    });
    if (!res.ok) {
        throw await documentWriteError(res, "Speichern fehlgeschlagen.");
    }
    try {
        return (await res.json()) as FassungGespeichert;
    } catch {
        return { documentId };
    }
}

export const documentEditingService = {
    /**
     * Bearbeitete Fassung speichern. Die bisherige wandert serverseitig in die
     * Historie — `Document` zeigt danach auf die neue Datei.
     *
     * Multipart statt Roh-Body, wie beim Upload. Die Grenze ist seit T-379
     * eine eigene (`MAX_SPEICHERN_BYTES`, 100 MB) mit eigenem nginx-Pfad.
     *
     * `note` ist der Kommentar, der in der Historie neben der abgelösten Fassung
     * steht; `fileName` benennt das Dokument beim Speichern zugleich um
     * (PO 30.08.2026); `rotate` dreht die Seiten dauerhaft (PO 14.09.2026).
     * Alles optional — ohne Angabe bleibt es bei den bisherigen Werten.
     */
    async saveVersion(
        documentId: number,
        bytes: Uint8Array,
        options?: FassungSpeichernAngaben & {
            /**
             * Grad im Uhrzeigersinn (Vielfaches von 90). Die Drehung schreibt der
             * Server ins PDF — im Browser dreht PDF.js nur die Ansicht.
             */
            rotate?: number;
            /**
             * Bereiche, die der Server beim Speichern ECHT aus dem PDF entfernt
             * (MuPDF, PO 28.09.2026). Leer/undefiniert → Feld fehlt.
             */
            bearbeitung?: PdfBearbeitung;
            /**
             * Ausschnitte (PNG) für „Ausschneiden & Verschieben“ (Teilplan 06 B):
             * `bearbeitung.einfuegen[i].bild` ist der Index in diese Liste.
             */
            bilder?: Blob[];
            /**
             * Das Dokument ist ein BILD und wird mit dieser Fassung zum PDF
             * (User 04.10.2026). Ohne diese Angabe weist der Server ein PDF für
             * ein Bild-Dokument ab.
             */
            ausBild?: boolean;
        },
    ): Promise<FassungGespeichert> {
        if (bytes.byteLength > MAX_SPEICHERN_BYTES) {
            throw new Error(`Die bearbeitete Datei ist zu groß zum Speichern (${hoechstensMb()}).`);
        }
        const ausschnitte = (options?.bilder ?? []).reduce((summe, b) => summe + b.size, 0);
        if (ausschnitte > MAX_AUSSCHNITTE_BYTES) {
            throw new Error(
                `Die verschobenen Ausschnitte sind zusammen zu groß zum Speichern (${hoechstensMb(MAX_AUSSCHNITTE_BYTES)}) — bitte kleinere oder weniger Bereiche verschieben.`,
            );
        }
        const fd = new FormData();
        // `bytes` ist ein View auf einen ggf. größeren ArrayBuffer — hier wird
        // genau der belegte Teil in einen eigenen Puffer kopiert, sonst landet
        // Füllmüll im Blob. (Kopie statt `bytes.buffer.slice()`: der Puffer eines
        // `Uint8Array` ist typseitig `ArrayBufferLike`, koennte also ein
        // `SharedArrayBuffer` sein — und der ist kein gueltiger `BlobPart`.)
        const body = new Uint8Array(bytes.byteLength);
        body.set(bytes);
        fd.append("file", new Blob([body], { type: "application/pdf" }), "bearbeitet.pdf");
        haengeAngabenAn(fd, options);
        if (options?.rotate) fd.append("rotate", String(options.rotate));
        if (options?.ausBild) fd.append("format", "pdf");
        if (options?.bearbeitung && !istLeer(options.bearbeitung)) {
            fd.append("bearbeitung", JSON.stringify(options.bearbeitung));
        }
        // Reihenfolge zählt: der Server liest die Dateien als Liste, `bild` ist der Index.
        (options?.bilder ?? []).forEach((bild, i) =>
            fd.append("bilder", new File([bild], `ausschnitt-${i + 1}.png`, { type: "image/png" })),
        );

        return sendeFassung(documentId, fd);
    },

    /**
     * Ein im PDF-Editor bearbeitetes BILD speichern — es bleibt ein Bild
     * (User 04.10.2026). Derselbe Endpunkt und dieselbe Fassungshistorie wie
     * bei PDFs; `format=bild` sagt dem Server, dass die Datei ein gerastertes
     * Bild ist. Drehung, Entfernen und Ausschnitte stecken schon in den Pixeln.
     */
    async saveImageVersion(
        documentId: number,
        bild: Blob,
        options?: FassungSpeichernAngaben,
    ): Promise<FassungGespeichert> {
        if (bild.size > MAX_SPEICHERN_BYTES) {
            throw new Error(
                `Das bearbeitete Bild ist zu groß zum Speichern (${hoechstensMb()}) — „Als PDF speichern“ geht meist trotzdem.`,
            );
        }
        const fd = new FormData();
        fd.append("file", bild, "bearbeitet");
        fd.append("format", "bild");
        haengeAngabenAn(fd, options);
        return sendeFassung(documentId, fd);
    },

    async listVersions(documentId: number): Promise<DocumentVersionInfo[]> {
        const res = await fetch(apiUrl(`documents/${documentId}/versions`));
        if (!res.ok) throw new Error(`Fassungen konnten nicht geladen werden: ${res.status}`);
        return res.json();
    },

    /** Frühere Fassung herunterladen (immer als Anhang ausgeliefert). */
    async fetchVersionBlob(documentId: number, versionNo: number): Promise<Blob> {
        const res = await fetch(apiUrl(`documents/${documentId}/versions/${versionNo}/download`));
        if (!res.ok) throw new Error(`Fassung konnte nicht geladen werden: ${res.status}`);
        return res.blob();
    },

    /**
     * Frühere Fassung zur aktuellen machen. Der bisherige Stand wird serverseitig
     * zuvor als neue Fassung gesichert — auch das ist also umkehrbar.
     */
    async restoreVersion(documentId: number, versionNo: number): Promise<void> {
        const res = await fetch(apiUrl(`documents/${documentId}/versions/${versionNo}/restore`), {
            method: "POST",
        });
        if (!res.ok) throw await documentWriteError(res, "Die Fassung konnte nicht wiederhergestellt werden.");
    },
};
